import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    ListSubheader,
    Button,
    List,
    ListItem,
    ListItemButton,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    Chip,
    ListItemText,
    OutlinedInput,
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { grey, red } from "@mui/material/colors";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import { checkValueExists, mergeSchema } from "../../../utils/dataSchema";
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import { spliceString } from "../../../utils/formatString";

import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';

import { useBranches, branchTypes, targetTypes as outletTargetTypes } from '../../../hooks/api/useBranches';
import { useBranchUsers, branchUserTypes, targetTypes as branchUserTargetTypes } from '../../../hooks/api/useBranchUsers';
import { useBusiness, businessTypes, targetTypes as businessTargetTypes } from "../../../hooks/api/useBusiness";
import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonRestricted from '../../common/CommonRestricted';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import { initialValues, initialPasswordValues, validationSchema, validationPasswordSchema } from "../../../schema/users/retailerSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import NewOutlet from "../../@user_branches/retailers/NewOutlet";
import UpdateOutlet from "../../@user_branches/retailers/UpdateOutlet";
import NewOutletUser from "../../@branch_users/retailers/NewOutletUser";
import UpdateOutletUser from "../../@branch_users/retailers/UpdateOutletUser";

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";
import { useAuthAccess } from "../../../hooks/useAuthAccess";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const StyledCard2 = styled(Card)(({ theme }) => ({
    padding: theme.spacing(0)
}));

const StyledListHeader = styled(ListSubheader)(({ theme }) => ({
    backgroundColor: grey[100],
    textTransform: 'capitalize',
}));

export default function UpdateRetailer({
    routeType,
    isOwnProfile = false
}) {
    const { t } = useTranslations({ ns: ["common", "@user"] })

    const { authData } = useAuthAccess()

    let { id } = useParams();

    // if the mode is only update own profile
    if (isOwnProfile) {
        id = authData?.profile?.user?.id
    }

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update, filterOperatorTypes } = useUsers({ userType: userTypes.retail });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.user, customFieldFunction: customFieldFunctions.user.retailer })

    const {
        list: listOutlets,
        remove: removeOutlet,
    } = useBranches({ branchType: branchTypes.retail, targetType: targetTypes.retail });

    const {
        list: listOutletUsers,
        remove: removeOutletUser,
    } = useBranchUsers({ branchUserType: branchUserTypes.retailer, targetType: branchUserTargetTypes.retailer });

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.retail.id)

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.user,
            moduleFunction: customFieldFunctions.user.retailer
        }
    )

    const { list: listBusiness } = useBusiness({ businessType: businessTypes.business });

    const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures()

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState()
    const { getRoute } = useSystemRoutes(routeType);
    const [dataOutlets, setDataOutlets] = useState([])
    const [dataOutletUsers, setDataOutletUsers] = useState([])
    const [dataBusiness, setDataBusiness] = useState([])
    const [customFields, setCustomFields] = useState([])

    const { getHtmlHead } = useSystemHead(routeType)

    const [checked, setChecked] = useState(false);

    const getSchema = () => {
        if (checked === true) {
            return mergeSchema(validationSchema, validationPasswordSchema);
        }
        return validationSchema
    }

    const handleAllowChangePassword = (event) => {
        setChecked(event.target.checked);
    };

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { retailer, custom_fields: savedCustomFields = [] } = data;

            const cf = await getInitialFormStateRows({
                savedCustomFields,
            })

            // const _cf = await fetchCustomFields({
            //     fieldModule: customFieldModules.user,
            //     moduleFunction: customFieldFunctions.user.retailer
            // })

            // const cf = getInitialFormStateRows({
            //     savedCustomFields,
            //     customFields: _cf
            // })

            setSavedValues({
                username: retailer.username,
                firstname: retailer.firstname,
                lastname: retailer.lastname,
                contactno: retailer.contact,
                address: retailer.address,
                email: retailer.email,
                status: retailer?.status === '1',
                companyname: retailer.company_name,
                regno: retailer.reg_no,
                code: retailer.code ? retailer.code : '',
                accountcode: retailer.account_code ? retailer.account_code : '',
                remark: retailer.remark,
                businessId: retailer?.business_id,
                differentBusiness: retailer?.is_different_business === '1',
                ...initialPasswordValues,
                rows: cf
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        try {

            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ rows })

            let data = {
                credential: {
                    firstname: currentValues?.firstname,
                    lastname: currentValues?.lastname,
                    password: currentValues?.password,
                    contact: currentValues?.contactno,
                    email: currentValues?.email,
                    status: currentValues?.status ? 1 : 0,
                },
                retailer: {
                    code: currentValues?.code,
                    account_code: currentValues?.accountcode,
                    company_name: currentValues?.companyname,
                    reg_no: currentValues?.regno,
                    remark: currentValues?.remark
                },
                props: {
                    salesperson: "",
                    business_id: currentValues?.differentBusiness ? currentValues?.businessId : ""
                },
                status: {
                    is_different_business: currentValues?.differentBusiness ? 1 : 0,
                },
                custom_fields: cf,

            }
            const response = await update({ id, data, scopeType: "", targetType: targetTypes.retail });

            promptMessage(true, `${response?.message}`)
            // navigate(getRoute("users/retailers"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const fetchDataOutlet = async (id) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "user_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            }

            // props.branchType = branchTypes.retail

            const { data } = await listOutlets(props)

            setDataOutlets(data?.branches)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataOutletUser = async (id) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "parent_user_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            }

            const { data } = await listOutletUsers(props)

            // console.log("data?.branch_users", data?.branch_users)

            setDataOutletUsers(data?.branch_users)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataBusiness = async () => {

        let filterValues = []

        try {

            filterValues.push({
                name: "status",
                operator: filterOperatorTypes.eq,
                value: '1',
            })

            const props = {
                limit: -1,
                offset: 0,
                filterValue: filterValues,
                sortInfo: {
                    id: "company_name",
                    dir: 0,
                }
            }

            const { data } = await listBusiness(props)

            setDataBusiness(data?.businesses)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    useEffect(() => {

        loadData(id);
        fetchDataOutlet(id)
        fetchDataOutletUser(id)
        fetchDataBusiness()

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const getAddNewComponent = (props) => {

        const { branchType, branchUserType } = props

        if (branchType === branchTypes.retail) {
            return (
                <NewOutlet
                    routeType={routeType}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        fetchDataOutlet(id)
                    }}
                />
            )
        }

        if (branchUserType === branchUserTypes.retailer) {
            return (
                <NewOutletUser
                    routeType={routeType}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        fetchDataOutletUser(id)
                    }}
                />
            )
        }

        return false

    }

    const handleItemNew = ({ branchType = '', branchUserType = '' }) => {

        if (!branchType && !branchUserType) return false

        openCustomDrawer({
            show: true,
            anchor: "right",
            title: `${branchType === branchTypes.retail ? t("newOutlet") : t("newOutletUser")}`,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody: getAddNewComponent({ branchType, branchUserType })

        })

        return true;

    }

    const handleItemEdit = ({ branchType = '', branchUserType = '', itemId }) => {

        if (!branchType && !branchUserType) return false;

        openCustomDrawer({
            show: true,
            anchor: "right",
            title: `${branchType === branchTypes.retail ? t("updateOutlet") : t('updateOutletUser')}`,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody:

                (branchType === branchTypes.retail) ?
                    <UpdateOutlet
                        routeType={routeType}
                        id={itemId}
                        handleOnSuccess={() => {
                            closeCustomDrawer()
                            fetchDataOutlet(id)
                        }}
                    />
                    :
                    <UpdateOutletUser
                        routeType={routeType}
                        id={itemId}
                        handleOnSuccess={() => {
                            closeCustomDrawer()
                            fetchDataOutletUser(id)
                        }}
                    />
        })

        return true;

    }

    const handleItemDelete = async ({ branchType = '', branchUserType = '', itemId }) => {

        setSystemLoading(true)

        try {

            if (branchType === branchTypes.retail) {
                let result = await removeOutlet({ ids: [itemId] })
                promptMessage(true, result?.message)
                fetchDataOutlet(id)
            }

            if (branchUserType === branchUserTypes.retailer) {
                let result = await removeOutletUser({ ids: [itemId] })
                promptMessage(true, result?.message)
                fetchDataOutletUser(id)
            }

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const RenderEmpty = ({ branchType = '' }) => {

        if (!branchType) return <></>

        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{
                            mt: 1
                        }}
                        onClick={() => handleItemNew({ branchType })}
                    >
                        {t('createNew')}
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderBranchUserEmpty = ({ branchUserType = '' }) => {

        if (!branchUserType) return <></>

        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{
                            mt: 1
                        }}
                        onClick={() => handleItemNew({ branchUserType })}
                    >
                        {t('createNew')}
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderChangePassword = () => {

        if (checked === false) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"password"}
                        label={t("password")}
                        type="password"
                        variant="outlined"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"passwordconfirm"}
                        label={t("confirmPassword")}
                        type="password"
                        variant="outlined"
                        value={values.passwordconfirm}
                        onChange={handleChange}
                        error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                        helperText={touched.passwordconfirm && errors.passwordconfirm}
                    />
                </Grid>
            </>
        )

    }

    const RenderCustomFieldSelection = () => {

        if (!values?.rows) return <></>

        const rows = values.rows

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customProperties')}
                        subTitle={t("provideCustomItemInformation")}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            values.rows.map((item, index) => {

                                const { rows } = values

                                let itemValues = rows.length ? rows[index] : {}

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                            // error: Boolean(itemErrors?.customValue),
                                                            // helperText: itemErrors?.customValue
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderFormAuth = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('credential')}
                        subTitle={t('accessAuthentication')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                            <TextField
                                disabled
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"username"}
                                label={t("username")}
                                type="text"
                                variant="outlined"
                                value={values.username}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        disabled={
                                            isOwnProfile
                                        }
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values?.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values?.status) ? t("enabled") : t("disabled")
                                }
                            />
                        </Grid>

                        {
                            (isOwnProfile === false) &&
                            <>

                                <Grid item xs={12} sm={12} md={12} textAlign="left">

                                    <Divider>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    onChange={handleAllowChangePassword}
                                                />
                                            }
                                            label={t("updatePassword")}
                                        />
                                    </Divider>

                                </Grid>

                                {
                                    RenderChangePassword()
                                }
                            </>
                        }

                    </Grid>
                </Grid>
            </>
        )

    }

    const RenderFormProfile = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('retailerProfile')}
                        subTitle={t('fullDetailsOfRetailerInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={6} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        disabled={
                                            isOwnProfile
                                        }
                                        sx={{
                                            ml: 2,
                                        }}
                                        name="differentBusiness"
                                        checked={Boolean(values.differentBusiness)}
                                        onChange={(event, checked) => {
                                            setFieldValue("differentBusiness", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.differentBusiness) ? t("differentBusiness") : t("sameBusiness")
                                }
                            />
                        </Grid>

                        {
                            (values?.differentBusiness === true) ?

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.businessId && Boolean(errors.businessId)}
                                    >
                                        <InputLabel id="businessId">{t('business')}</InputLabel>
                                        <Select
                                            disabled={values?.differentBusiness === false}
                                            labelId="businessId"
                                            label={t("business")}
                                            name={"businessId"}
                                            onChange={(event) => {
                                                setFieldValue("businessId", event.target.value)
                                            }
                                            }
                                            // value={values?.businessId}
                                            value={
                                                checkValueExists({
                                                    dataSearch: dataBusiness,
                                                    valueToCompare: values?.businessId
                                                })
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                dataBusiness.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>{i.company_name}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.businessId && errors.businessId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                :
                                <Grid item xs={12} sm={6} md={6}>
                                    <></>
                                </Grid>

                        }

                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"firstname"}
                                label={t("firstName")}
                                type="text"
                                autoFocus
                                variant="outlined"
                                value={values?.firstname}
                                onChange={handleChange}
                                error={touched.firstname && Boolean(errors.firstname)}
                                helperText={touched.firstname && errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"lastname"}
                                label={t("lastName")}
                                type="text"
                                variant="outlined"
                                value={values?.lastname}
                                onChange={handleChange}
                                error={touched.lastname && Boolean(errors.lastname)}
                                helperText={touched.lastname && errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"companyname"}
                                label={t("companyName")}
                                type="text"
                                variant="outlined"
                                value={values?.companyname}
                                onChange={handleChange}
                                error={touched.companyname && Boolean(errors.companyname)}
                                helperText={touched.companyname && errors.companyname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"regno"}
                                label={t("regNo")}
                                type="text"
                                variant="outlined"
                                value={values?.regno}
                                onChange={handleChange}
                                error={touched.regno && Boolean(errors.regno)}
                                helperText={touched.regno && errors.regno}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"code"}
                                label={t("code")}
                                type="text"
                                variant="outlined"
                                value={values?.code}
                                onChange={handleChange}
                                error={touched.code && Boolean(errors.code)}
                                helperText={touched.code && errors.code}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"accountcode"}
                                label={t("accountCode")}
                                type="text"
                                variant="outlined"
                                value={values?.accountcode}
                                onChange={handleChange}
                                error={touched.accountcode && Boolean(errors.accountcode)}
                                helperText={touched.accountcode && errors.accountcode}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"email"}
                                label={t("email")}
                                type="email"
                                variant="outlined"
                                value={values?.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"contactno"}
                                label={t("contactNo")}
                                type="text"
                                variant="outlined"
                                value={values?.contactno}
                                onChange={handleChange}
                                error={touched.contactno && Boolean(errors.contactno)}
                                helperText={touched.contactno && errors.contactno}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"remark"}
                                label={t("remark")}
                                type="text"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={values?.remark ? values.remark : ""}
                                onChange={handleChange}
                                error={touched.remark && Boolean(errors.remark)}
                                helperText={touched.remark && errors.remark}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderOutlet = () => {

        if (!dataOutlets.length) return <RenderEmpty branchType={branchTypes.retail} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    disablePadding
                >
                    <StyledListHeader>{t('outlets')}</StyledListHeader>
                    {
                        dataOutlets.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}
                                            sx={{
                                                fontSize: 15
                                            }}
                                        >
                                            {
                                                `${i?.branch_name} (${i?.branch_code})`
                                            }

                                            {
                                                i?.status === "0" &&
                                                <Tooltip
                                                    title={t("disabled")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                i?.is_hq === "1" &&
                                                <Tooltip
                                                    title={t("mainOutlet")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.default} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                (i?.type === "branch" || i?.type === "branch_shipping") &&
                                                <Tooltip
                                                    title={t("outlet")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.warehouse} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                (i?.type === "branch_shipping" || i?.type === "shipping_address") &&
                                                <Tooltip
                                                    title={t("shippingAddress")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.logistic} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>

                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].retail.id, functionType: functionTypes.pageUpdate.id }) &&

                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ branchType: branchTypes.retail, itemId: i.id })}
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].retail.id, functionType: functionTypes.remove.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ itemId: i.id, branchType: branchTypes.retail })}
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                {
                    isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].retail.id, functionType: functionTypes.pageNew.id }) &&

                    <Box
                        sx={{ m: 1 }}
                    >
                        <Button
                            variant={"outlined"}
                            size="medium"
                            fullWidth
                            onClick={() => handleItemNew({ branchType: branchTypes.retail })}
                        >
                            <CommonIcons name={icons.add} />
                            <Box sx={{ ml: 1 }}>
                                {t('new')}
                            </Box>
                        </Button>
                    </Box>
                }
            </StyledCard2>
        )
    }

    const RenderOutletUser = () => {

        if (!dataOutletUsers.length) return <RenderBranchUserEmpty branchUserType={branchUserTypes.retailer} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    disablePadding
                >
                    <StyledListHeader>{t("users")}</StyledListHeader>
                    {
                        dataOutletUsers.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}
                                            sx={{
                                                fontSize: 15
                                            }}
                                        >
                                            {
                                                `${i?.firstname} ${i?.lastname} (${i.username})`
                                            }

                                            {
                                                i?.status !== "1" &&

                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                </Box>
                                            }

                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>

                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].retail.id, functionType: functionTypes.pageUpdate.id }) &&

                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ branchUserType: branchUserTypes.retailer, itemId: i.user_id })}
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].retail.id, functionType: functionTypes.remove.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ itemId: i.user_id, branchUserType: branchUserTypes.retailer })}
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                {
                    isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].retail.id, functionType: functionTypes.pageNew.id }) &&

                    <Box
                        sx={{ m: 1 }}
                    >
                        <Button
                            variant={"outlined"}
                            size="medium"
                            fullWidth
                            onClick={() => handleItemNew({ branchUserType: branchUserTypes.retailer })}
                        >
                            <CommonIcons name={icons.add} />
                            <Box sx={{ ml: 1 }}>
                                {t('new')}
                            </Box>
                        </Button>
                    </Box>
                }

            </StyledCard2>
        )
    }

    const RenderOutletList = () => {

        // if(!isOwnProfile) return <></>

        if (!values) return <></>

        return (
            <StyledCard>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('outlets')}
                            subTitle={t('listOfOutletsInformation')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        {
                            RenderOutlet()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderOutletUserList = () => {

        // if(!isOwnProfile) return <></>

        if (!values) return <></>

        return (
            <StyledCard
                sx={{
                    mt: 2
                }}
            >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('outletUsers')}
                            subTitle={t('assignedUsersInformation')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        {
                            RenderOutletUser()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0}>
                        {
                            RenderFormAuth()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {
                            RenderFormProfile()
                        }

                        {
                            RenderCustomFieldSelection()
                        }
                    </Grid>


                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>

                </Grid>
            </form>

        )
    }

    const getBreadcumbs = () => {
        if (isOwnProfile) {
            return [
                {
                    displayText: t("updateProfile"),
                    props: {
                        color: 'secondary.main'
                    }
                },
            ]
        }

        return [
            {
                displayText: t("retailers"),
                props: {
                    href: getRoute(`users/retailers`)

                }
            },
            {
                displayText: t("updateRetailer"),
                props: {
                    color: 'secondary.main'
                }
            },
        ]
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateRetailer })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={
                    isOwnProfile ? t(`updateProfile`) : t(`updateRetailer`)
                }
                breadcrumbs={
                    getBreadcumbs()
                }
            />

            {

                (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) || isOwnProfile === true) ?
                    RenderForm()
                    :
                    <></>
            }

            {
                (isOwnProfile === false) &&
                <>
                    {
                        RenderOutletList()
                    }

                    {
                        RenderOutletUserList()
                    }

                </>
            }

        </Box>
    )
}